/**
 * Document projection — the single seam between the element/geometry coordinate
 * frame and the document-pixel frame used for rendering and pointer input.
 *
 * Two frames exist in the editor:
 *
 * - **Document-pixel frame**: origin (0,0) at the top-left corner of the bleed box.
 *   The document canvas DOM element is laid out in this frame; chrome/overlays
 *   (bleed, trim, spine, columns) are positioned here. The spine sits at
 *   `bleedMargin + pageTrimWidth` and the top of trim at `bleedMargin`.
 *
 * - **Centered frame** (legacy / NSD): origin at the spine on X (left page negative,
 *   right page positive). This is the frame the backend checkpoint and legacy NSD
 *   store, so making it the element frame removes per-element conversion on load/save.
 *
 * A projection is a pure translation. Width/height/rotation are frame-invariant and
 * are never touched here — only x/y positions move.
 *
 * Phase 1 (current): {@link IDENTITY_PROJECTION} keeps the element frame identical to
 * the document-pixel frame, so wiring this seam in changes no behavior. Phase 3 flips
 * the active projection to {@link spreadCenterProjection} once the centered frame is
 * confirmed and the persisted store is migrated.
 */

import type { SpreadDimensions } from '@/lib/document/types/spread-dimensions'
import type { ProjectionPoint } from './types/projection-point'
import type { ProjectionRect } from './types/projection-rect'
import type { DocumentProjection } from '@/lib/canvas/types/document-projection'

/** No-op projection: element frame == document-pixel frame (today's behavior). */
export const IDENTITY_PROJECTION: DocumentProjection = { offsetX: 0, offsetY: 0 }

/** Centered (element) point → document-pixel point. */
export function centeredToDocPx(p: ProjectionPoint, proj: DocumentProjection): ProjectionPoint {
  return { x: p.x + proj.offsetX, y: p.y + proj.offsetY }
}

/** Document-pixel point → centered (element) point. Inverse of {@link centeredToDocPx}. */
export function docPxToCentered(p: ProjectionPoint, proj: DocumentProjection): ProjectionPoint {
  return { x: p.x - proj.offsetX, y: p.y - proj.offsetY }
}

/** Centered rect → document-pixel rect. Only x/y translate; width/height are invariant. */
export function centeredRectToDocPx(r: ProjectionRect, proj: DocumentProjection): ProjectionRect {
  return { x: r.x + proj.offsetX, y: r.y + proj.offsetY, width: r.width, height: r.height }
}

/** Document-pixel rect → centered rect. Inverse of {@link centeredRectToDocPx}. */
export function docPxRectToCentered(r: ProjectionRect, proj: DocumentProjection): ProjectionRect {
  return { x: r.x - proj.offsetX, y: r.y - proj.offsetY, width: r.width, height: r.height }
}

/**
 * Phase 3 target projection: maps the centered/legacy frame into the document-pixel box.
 *
 * X: spine (centered 0) → `bleedMargin + pageTrimWidth` in document pixels.
 * Y: top of trim (centered 0) → `bleedMargin` in document pixels.
 *
 * NOTE: the Y origin is the one piece still unconfirmed against legacy (folio elements
 * sit near the page bottom, which is consistent with a top-anchored Y but must be
 * verified empirically before this projection is activated). Until then this factory is
 * provided for tests and wiring only — the active projection remains
 * {@link IDENTITY_PROJECTION}.
 */
export function spreadCenterProjection(dims: SpreadDimensions): DocumentProjection {
  const pageTrimWidth = Math.max(0, Math.floor(dims.trimWidth / 2))

  return {
    offsetX: dims.bleedMargin + pageTrimWidth,
    offsetY: dims.bleedMargin,
  }
}
